<template>
  <ExpandableListItem
    :color="color"
    :expanded="expanded"
    :items="items"
    draggable
    :dragDisabled="device.touch"
    itemKey="id"
    testId="service-package"
    :moreOptions="['edit', 'delete', 'sort']"
    @click="$emit('toggle')"
    @moreSelect="onMoreSelect"
    @update:items="onReorder"
  >
    <template #left>
      <BaseText
        bold
        :color="textColor"
        v-test="'service-package-list-item-name'"
      >
        {{ servicePackage.name }}
      </BaseText>
      <BaseText
        v-if="$screen === 's'"
        :color="textColor"
        v-test="'service-package-list-item-count'"
      >
        ({{ servicePackage.servicePackageServices.length }})
      </BaseText>
      <BaseText
        v-else
        :color="textColor"
        v-test="'service-package-list-item-count'"
      >
        ({{ servicePackage.servicePackageServices.length }}
        {{ t('global.items.service', servicePackage.servicePackageServices.length) }})
      </BaseText>
    </template>
    <template
      #right
      v-if="$screen !== 's'"
    >
      <div :class="$style.duration">
        <BaseText
          :color="textColor"
          v-test="'service-package-list-item-duration'"
        >
          {{ durationText }}
        </BaseText>
      </div>
      <div :class="$style.bookable">
        <BaseIcon
          v-if="servicePackage.bookable"
          name="globe"
          size="s"
          :tooltip="{ text: t('package.bookable_heading'), touch: true }"
          v-test="'service-package-list-item-online'"
        />
      </div>
      <div :class="$style.price">
        <BaseText
          :color="textColor"
          v-test="'service-package-list-item-price'"
        >
          {{ filters.currency(servicePackage.price) }}
        </BaseText>
      </div>
    </template>
    <template
      v-if="sortMode"
      #actions
    >
      <div :class="$style.sortOrder">
        <BaseCounter
          v-model="sortOrderValue"
          :minValue="0"
          :maxValue="listLength - 1"
          hideValue
          reverse
          v-test="'service-package-sort-order'"
        />
      </div>
    </template>
    <template #row="{ item }">
      <div :class="$style.section">
        <BaseText v-test="'service-package-list-item-service-name'">
          {{ serviceLabel(item.service.id) }}
        </BaseText>
      </div>
      <div :class="$style.section">
        <div :class="$style.duration">
          <BaseText
            color="secondary"
            v-test="'service-package-list-item-service-duration'"
          >
            {{ serviceDurationText(item.service.id) }}
          </BaseText>
        </div>
        <div :class="$style.price">
          <BaseText v-test="'service-package-list-item-service-price'">
            {{ filters.currency(item.price) }}
          </BaseText>
        </div>
      </div>
    </template>
    <template #footer>
      <div :class="$style.section">
        <BaseHeading
          size="s"
          v-test="'service-package-list-item-total-label'"
        >
          {{ t('package.price') }}
        </BaseHeading>
      </div>
      <div :class="$style.section">
        <BaseText
          v-if="hasSavings"
          color="secondary"
          lineThrough
          v-test="'service-package-list-item-original-price'"
        >
          {{ filters.currency(originalPrice) }}
        </BaseText>
        <BaseText
          bold
          v-test="'service-package-list-item-total-price'"
        >
          {{ filters.currency(servicePackage.price) }}
        </BaseText>
      </div>
    </template>
  </ExpandableListItem>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import filters from '@/filters';
import { flash, modal } from '@/helpers/ui';
import ExpandableListItem from '@/modules/admin/services/shared/ExpandableListItem.vue';
import { useServicesStore } from '@/stores/services';
import type { ServicePackage } from '@/types';
import { device } from '@/user-context';
import { DELETE_SERVICE_PACKAGE, UPDATE_SERVICE_PACKAGE } from './graphql';

type Actions = 'edit' | 'delete' | 'sort';

const { t } = useI18n();
const router = useRouter();

const props = defineProps<{
  servicePackage: ServicePackage;
  expanded: boolean;
  serviceLookup: Map<number, { label: string; duration: number }>;
  index: number;
  listLength: number;
}>();

const emit = defineEmits<{
  toggle: [];
  updateSortOrder: [value: number];
}>();

const sortMode = inject('services-category-sort-order', ref(false));

const sortOrderValue = computed({
  get: () => props.index,
  set: (value: number) => emit('updateSortOrder', value),
});

const { mutate: deleteServicePackage, onDone: onDeleted } = useMutation(
  DELETE_SERVICE_PACKAGE,
  {
    refetchQueries: ['getServicePackages'],
  },
);
const { mutate: updateServicePackage, onDone: onUpdated } = useMutation(
  UPDATE_SERVICE_PACKAGE,
  {
    refetchQueries: ['getServicePackages'],
  },
);

// refetchQueries only refreshes this module's queries, the calendar reads the packages from the store
const { getServicePackages } = useServicesStore();
[onDeleted, onUpdated].forEach((onDone) => {
  onDone(() => getServicePackages());
});

const serviceLabel = (serviceId: number) =>
  props.serviceLookup.get(serviceId)?.label ?? '';

const serviceDurationText = (serviceId: number) => {
  const duration = props.serviceLookup.get(serviceId)?.duration ?? 0;
  return `${duration} ${t('global.items.minute_short', duration)}`;
};

const packageDuration = computed(() =>
  props.servicePackage.servicePackageServices.reduce(
    (sum, item) =>
      sum + (props.serviceLookup.get(item.service.id)?.duration ?? 0),
    0,
  ),
);

const durationText = computed(
  () =>
    `${packageDuration.value} ${t('global.items.minute', packageDuration.value)}`,
);

const color = computed(() => props.servicePackage.category?.color ?? null);

const textColor = computed(() => (color.value ? 'fixed' : undefined));

const originalPrice = computed(() =>
  props.servicePackage.servicePackageServices.reduce(
    (sum, item) => sum + item.originalPrice,
    0,
  ),
);

const hasSavings = computed(
  () => props.servicePackage.price < originalPrice.value,
);

// Bound to <ExpandableListItem>'s draggable list separately from
// servicePackage.servicePackageServices (rather than directly), so a drag
// can reorder it immediately instead of snapping back to the old order
// while the reorder mutation is still in flight.
const items = ref(props.servicePackage.servicePackageServices);

watch(
  () => props.servicePackage.servicePackageServices,
  (newItems) => {
    items.value = newItems;
  },
);

const editPackage = () => {
  router.push({
    name: 'admin-service-package',
    params: { packageId: props.servicePackage.id },
  });
};

const onReorder = (newItems: ServicePackage['servicePackageServices']) => {
  items.value = newItems;

  updateServicePackage({
    input: {
      id: props.servicePackage.id,
      name: props.servicePackage.name,
      categoryId: props.servicePackage.category?.id ?? null,
      description: props.servicePackage.description ?? null,
      bookable: props.servicePackage.bookable,
      pricingType: props.servicePackage.pricingType,
      fixedPrice: props.servicePackage.fixedPrice,
      discountPercentage: props.servicePackage.discountPercentage,
      servicePackageServicesAttributes: newItems.map((item, index) => ({
        id: item.id,
        serviceId: item.service.id,
        originalPrice: item.originalPrice,
        sortOrder: index,
      })),
    },
  }).then(() => {
    flash(t('global.flash.services_updated'));
  });
};

const onMoreSelect = (action: string) => {
  switch (action as Actions) {
    case 'edit':
      editPackage();
      break;

    case 'delete':
      modal('confirmation', {
        type: 'delete',
        item: props.servicePackage.name,
        subMessage: t('package.delete_warning'),
      }).then(() => {
        deleteServicePackage({
          input: { id: props.servicePackage.id },
        }).then(() => {
          flash(t('global.flash.package_deleted'));
        });
      });
      break;

    case 'sort':
      sortMode.value = true;
      break;

    default:
      break;
  }
};
</script>

<style
  lang="scss"
  module
>
.section {
  display: flex;
  align-items: center;
  gap: $spacing;
  padding: $spacing;
}

.duration {
  white-space: nowrap;
}

.price {
  width: 57px;
  text-align: right;
}

.sortOrder {
  margin-top: $spacing * -0.5;
  margin-bottom: $spacing * -0.5;
}

.bookable {
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
